import React, { useState } from 'react';
import { ArrowDropDown, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Popover } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import moment from 'moment';
import {
  buttonStyles,
  labelStyles,
  navigationContainerStyles,
  toolbarContainerStyles,
  viewButtonStyles,
} from './CalendarToolbar.styles';
import { CalendarToolbarProps } from './CalendarToolbar.types';
import { CALENDAR_NAV_NEXT, CALENDAR_NAV_PREV, CALENDAR_NAV_TODAY, CALENDAR_VIEW_MONTH, CALENDAR_VIEW_WEEK } from '../../common/constants/calendar';

const getLabel = (date: Date, view: string): string => {
  if (view === CALENDAR_VIEW_WEEK) {
    return `${moment(date).startOf('week').format('MMM D')} – ${moment(date).endOf('week').format('MMM D, YYYY')}`;
  }
  return moment(date).format('dddd, MMMM D, YYYY');
};

const CalendarToolbar = ({ date, view, onNavigate, onViewChange, onDateSelect, views }: CalendarToolbarProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pickerView, setPickerView] = useState<'month' | 'year'>('month');
  const [pendingDate, setPendingDate] = useState<Date>(date);

  const handleLabelClick = (event: React.MouseEvent<HTMLElement>) => {
    setPendingDate(date);
    setPickerView('month');
    setAnchorEl(event.currentTarget);
  };

  const handlePickerChange = (newDate: Date | null) => {
    if (newDate) {
      setPendingDate(newDate);
      if (pickerView === 'month') {
        onDateSelect?.(newDate);
        setAnchorEl(null);
      }
    }
  };

  const handleOk = () => {
    onDateSelect?.(pendingDate);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={toolbarContainerStyles}>
      <Box sx={navigationContainerStyles}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onNavigate(CALENDAR_NAV_TODAY)}
          sx={buttonStyles}
        >
          Today
        </Button>
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => onNavigate(CALENDAR_NAV_PREV)}>
            <ChevronLeft fontSize="small" />
          </Button>
          <Button onClick={() => onNavigate(CALENDAR_NAV_NEXT)}>
            <ChevronRight fontSize="small" />
          </Button>
        </ButtonGroup>
        {view === CALENDAR_VIEW_MONTH && onDateSelect ? (
          <>
            <Button
              variant="text"
              onClick={handleLabelClick}
              endIcon={<ArrowDropDown />}
              sx={{ fontWeight: 500, fontSize: '1.1rem', ml: 2, textTransform: 'none', color: 'text.primary' }}
            >
              {moment(date).format('MMMM YYYY')}
            </Button>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCancel}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <DateCalendar
                value={pendingDate}
                onChange={handlePickerChange}
                views={['month', 'year']}
                openTo="month"
                onViewChange={newView => setPickerView(newView as 'month' | 'year')}
                sx={{ height: 'auto' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, pb: 2 }}>
                <Button size="small" onClick={handleCancel}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleOk}>OK</Button>
              </Box>
            </Popover>
          </>
        ) : (
          <Box sx={labelStyles}>
            {view === CALENDAR_VIEW_MONTH ? moment(date).format('MMMM YYYY') : getLabel(date, view)}
          </Box>
        )}
      </Box>
      <ButtonGroup size="small" variant="outlined">
        {views.map(v => (
          <Button
            key={v}
            onClick={() => onViewChange(v)}
            variant={view === v ? 'contained' : 'outlined'}
            sx={viewButtonStyles}
          >
            {v}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
};

export default CalendarToolbar;
